import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { patch } from "../lib/api";
import useStoreCategories from "../lib/useStoreCategories";

export default function HomeCategoryPicker({
  user,
  currentCategory,
  onSelecting,
  onSelected,
  onFailed,
  triggerLabel = "Store types",
  triggerClassName = "rounded-lg px-2 py-2 text-sm font-semibold underline underline-offset-4 focus-visible:outline focus-visible:outline-2",
}) {
  const dialog = useRef(null);
  const { categories, loading, error } = useStoreCategories();
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [pendingLabel, setPendingLabel] = useState("");
  const choose = async (category) => {
    if (busy) return;
    setBusy(true);
    setSaveError("");
    setPendingLabel(category.label);
    onSelecting(category.label);
    try {
      await patch("/customers/me/category", {
        currentProductCategory: category.value,
      });
      onSelected(category);
      window.dispatchEvent(new Event("catalog-preference-changed"));
      dialog.current.close();
    } catch (error) {
      setSaveError(
        error.response?.data?.message ||
          "Unable to change store type. Please try again.",
      );
      onFailed();
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={() => dialog.current.showModal()}
        className={triggerClassName}
      >
        {triggerLabel}
      </button>
      <dialog
        ref={dialog}
        aria-labelledby="category-picker-title"
        onCancel={(event) => {
          if (busy) event.preventDefault();
        }}
        className="w-[calc(100%_-_2rem)] max-w-lg rounded-2xl bg-white p-0 text-slate-900 shadow-xl [text-shadow:none] backdrop:bg-black/50"
      >
        <div className="p-5 sm:p-6">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h2 id="category-picker-title" className="text-xl font-bold text-green-900 [text-shadow:none]">
             Store types
            </h2>
            <button
              type="button"
              disabled={busy}
              onClick={() => dialog.current.close()}
              className="rounded-lg border px-3 py-2 disabled:opacity-50"
            >
              Close
            </button>
          </div>
          {loading && <p role="status">Loading categories...</p>}
          {(error || saveError) && (
            <p role="alert" className="mb-3 text-sm text-red-700">
              {saveError || error}
            </p>
          )}
          {busy && (
            <p role="status" className="mb-4 rounded-lg bg-primary/5 p-3">
              Exploring {pendingLabel} at bigcircle.ph
            </p>
          )}
          {user?.role !== "CUSTOMER" && (
            <p className="mb-4 text-sm">
              <Link className="font-semibold underline" to="/profile">
                Sign in to your customer account
              </Link>{" "}
              to explore other store types.
            </p>
          )}
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {categories.map((category) => (
              <button
                key={category.value}
                type="button"
                disabled={busy || user?.role !== "CUSTOMER"}
                aria-pressed={currentCategory === category.value}
                onClick={() => choose(category)}
                className="flex w-full items-center gap-3 rounded-xl border p-3 text-left hover:bg-slate-50 disabled:opacity-50"
              >
                <img
                  src={category.banner}
                  alt=""
                  className="h-14 w-20 rounded-lg object-cover"
                />
                <span>
                  <span className="block font-semibold">
                    {category.label}
                    {currentCategory === category.value ? " (Current)" : ""}
                  </span>
                  <span className="block text-sm text-slate-500">
                    {category.slogan}
                  </span>
                </span>
              </button>
            ))}
          </div>
          {!loading && !error && !categories.length && (
            <p>No categories are available yet.</p>
          )}
        </div>
      </dialog>
    </>
  );
}
